'use client';

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  Badge,
  BodyDefault,
  FeatureAnalysisCredentialBadge,
  HeadingItem,
  IconButton,
  LabelDefault,
  MetaDefault,
  MonoSmall,
  Spinner,
  Tooltip,
  toasterApi,
} from '@terra/ui-design-system';
import { Box, HStack, VStack, token } from '@terra/ui-styled-system';
import {
  CheckmarkCircle02Icon,
  Copy01Icon,
  ViewIcon,
  ViewOffSlashIcon,
} from '@terra/ui-icons';
import { MfaProviderSchema } from '@terra/shared-schemas-orchestrator';
import { useTranslations } from 'next-intl';
import type { ApplicationCredential } from '../../../hooks/useApplicationCredentials';
import { getCredentialDisplayName } from './filterCredentialsBySearch';
import { useCredentialMenuOtp } from './useCredentialMenuOtp';

type CredentialsMenuCardProps = {
  applicationId?: string;
  credential: ApplicationCredential;
  isMenuOpen: boolean;
};

type CopyField = 'username' | 'password' | 'otp';

type CredentialMenuDetailRowProps = {
  label: string;
  children: ReactNode;
  trailing?: ReactNode;
};

type VerificationBadgeConfig = {
  label: string;
  bg?: string;
  color?: string;
};

function CredentialMenuDetailRow({
  label,
  children,
  trailing,
}: CredentialMenuDetailRowProps) {
  return (
    <HStack gap={2} alignItems="center" w="full" minH="8">
      <Box w="20" flexShrink={0}>
        <LabelDefault color="fg.neutral.muted">{label}</LabelDefault>
      </Box>
      <Box flex={1} minW={0}>
        {children}
      </Box>
      {trailing}
    </HStack>
  );
}

function getVerificationBadgeConfig({
  status,
  labels,
}: {
  status?: 'PENDING' | 'VERIFYING' | 'VERIFIED' | 'FAILED';
  labels: {
    notVerified: string;
    verified: string;
    verificationFailed: string;
    verifying: string;
  };
}): VerificationBadgeConfig {
  if (status === 'VERIFYING') {
    return {
      label: labels.verifying,
      bg: token('colors.bg.accent.light'),
      color: token('colors.fg.accent.muted'),
    };
  }

  if (status === 'VERIFIED') {
    return {
      label: labels.verified,
      bg: token('colors.bg.neutral.success'),
    };
  }

  if (status === 'FAILED') {
    return {
      label: labels.verificationFailed,
      bg: token('colors.bg.neutral.error'),
    };
  }

  return {
    label: labels.notVerified,
  };
}

function CopyIconButton({
  ariaLabel,
  copied,
  disabled,
  onCopy,
}: {
  ariaLabel: string;
  copied: boolean;
  disabled?: boolean;
  onCopy: () => void;
}) {
  return (
    <Tooltip title={ariaLabel}>
      <IconButton
        variant="ghost"
        size="xs"
        aria-label={ariaLabel}
        disabled={disabled}
        onClick={onCopy}
        flexShrink={0}
      >
        {copied ? <CheckmarkCircle02Icon /> : <Copy01Icon />}
      </IconButton>
    </Tooltip>
  );
}

export function CredentialsMenuCard({
  applicationId,
  credential,
  isMenuOpen,
}: CredentialsMenuCardProps) {
  const tMenu = useTranslations('layout.credentialsMenu');
  const tCredentials = useTranslations('designSystem.applicationCredentials');
  const tCommon = useTranslations('common');
  const [copiedField, setCopiedField] = useState<CopyField | null>(null);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);

  const verificationStatus = credential.verification?.status;
  const activeOtpConfig = useMemo(
    () =>
      credential.mfaConfig.find(
        (config) =>
          config.providerType === MfaProviderSchema.enum.OTP && config.isActive,
      ),
    [credential.mfaConfig],
  );

  const {
    otpCode,
    secondsLeft,
    isLoading: isOtpLoading,
    hasError: hasOtpError,
  } = useCredentialMenuOtp({
    applicationId,
    credentialId: credential.id,
    mfaConfigId: activeOtpConfig?.id ?? '',
    enabled: isMenuOpen && !!activeOtpConfig,
  });

  const hasShownOtpErrorToastRef = useRef(false);

  useEffect(() => {
    if (!isMenuOpen) {
      hasShownOtpErrorToastRef.current = false;

      return;
    }

    if (!hasOtpError || hasShownOtpErrorToastRef.current) {
      return;
    }

    hasShownOtpErrorToastRef.current = true;
    toasterApi.error({ title: tMenu('otpFetchFailed') });
  }, [hasOtpError, isMenuOpen, tMenu]);

  const verificationBadge = getVerificationBadgeConfig({
    status: verificationStatus,
    labels: {
      notVerified: tMenu('notVerified'),
      verified: tMenu('verified'),
      verificationFailed: tMenu('verificationFailed'),
      verifying: tMenu('verifying'),
    },
  });

  const roleValue = credential.role.trim();
  const usernameValue = credential.username.trim();
  const passwordValue = credential.password ?? '';
  const formattedOtpCode = otpCode.replace(/(\d{3})(?=\d)/g, '$1 ').trim();
  const showOtpRow = !!activeOtpConfig;
  const canCopyOtp = formattedOtpCode.length > 0 && !isOtpLoading;

  const handleCopy = useCallback(
    async ({ field, value }: { field: CopyField; value: string }) => {
      if (!value) {
        return;
      }

      try {
        await navigator.clipboard.writeText(value);
        setCopiedField(field);
        toasterApi.success({ title: tMenu('copied') });
        setTimeout(() => setCopiedField(null), 2000);
      } catch (error) {
        console.error('Failed to copy credential field', error);
        toasterApi.error({ title: tMenu('copyFailed') });
      }
    },
    [tMenu],
  );

  const copyLabel = tCommon('copy');
  const showPasswordLabel = tCommon('showPassword');
  const hidePasswordLabel = tCommon('hidePassword');

  return (
    <Box
      w="full"
      borderWidth="1px"
      borderStyle="solid"
      borderColor="border.neutral.subtle"
      borderRadius="l2"
      bg="bg.neutral.default"
      px="2.5"
      py="2"
    >
      <VStack alignItems="stretch" gap={1} w="full">
        <HStack gap={2} alignItems="center" minH="8">
          <HeadingItem flex={1} minW={0} truncate color="fg.neutral.default">
            {getCredentialDisplayName({
              credential,
              noUsernameLabel: tCredentials('noUsername'),
            })}
          </HeadingItem>
          <FeatureAnalysisCredentialBadge
            generatedBy={credential.generatedBy}
          />
          <Badge
            variant="fill"
            size="sm"
            bg={verificationBadge.bg}
            color={verificationBadge.color}
            flexShrink={0}
          >
            {verificationBadge.label}
          </Badge>
        </HStack>

        <VStack alignItems="stretch" gap="0.5" w="full">
          {roleValue ? (
            <CredentialMenuDetailRow label={tCredentials('role')}>
              <Badge variant="fill" size="sm">
                {roleValue}
              </Badge>
            </CredentialMenuDetailRow>
          ) : null}

          {usernameValue ? (
            <CredentialMenuDetailRow
              label={tCredentials('username')}
              trailing={
                <CopyIconButton
                  ariaLabel={copyLabel}
                  copied={copiedField === 'username'}
                  onCopy={() =>
                    void handleCopy({
                      field: 'username',
                      value: usernameValue,
                    })
                  }
                />
              }
            >
              <Tooltip title={usernameValue}>
                <BodyDefault color="fg.neutral.default" truncate>
                  {usernameValue}
                </BodyDefault>
              </Tooltip>
            </CredentialMenuDetailRow>
          ) : null}

          {passwordValue ? (
            <CredentialMenuDetailRow
              label={tCredentials('password')}
              trailing={
                <HStack gap="0.5" flexShrink={0}>
                  <Tooltip
                    title={
                      isPasswordVisible ? hidePasswordLabel : showPasswordLabel
                    }
                  >
                    <IconButton
                      variant="ghost"
                      size="xs"
                      aria-label={
                        isPasswordVisible
                          ? hidePasswordLabel
                          : showPasswordLabel
                      }
                      onClick={() =>
                        setIsPasswordVisible((visible) => !visible)
                      }
                    >
                      {isPasswordVisible ? <ViewOffSlashIcon /> : <ViewIcon />}
                    </IconButton>
                  </Tooltip>
                  <CopyIconButton
                    ariaLabel={copyLabel}
                    copied={copiedField === 'password'}
                    onCopy={() =>
                      void handleCopy({
                        field: 'password',
                        value: passwordValue,
                      })
                    }
                  />
                </HStack>
              }
            >
              <BodyDefault color="fg.neutral.default" truncate>
                {isPasswordVisible ? passwordValue : '*****'}
              </BodyDefault>
            </CredentialMenuDetailRow>
          ) : null}

          {showOtpRow ? (
            <CredentialMenuDetailRow
              label={tMenu('otp')}
              trailing={
                <HStack gap="0.5" alignItems="center" flexShrink={0}>
                  {secondsLeft > 0 ? (
                    <MetaDefault color="fg.neutral.subtle">
                      {tMenu('otpSecondsRemaining', { seconds: secondsLeft })}
                    </MetaDefault>
                  ) : null}
                  {canCopyOtp ? (
                    <CopyIconButton
                      ariaLabel={copyLabel}
                      copied={copiedField === 'otp'}
                      onCopy={() =>
                        void handleCopy({
                          field: 'otp',
                          value: otpCode.replace(/\s+/g, ''),
                        })
                      }
                    />
                  ) : null}
                </HStack>
              }
            >
              {isOtpLoading && !formattedOtpCode ? (
                <Spinner size="sm" />
              ) : (
                <MonoSmall color="fg.neutral.default" truncate>
                  {formattedOtpCode}
                </MonoSmall>
              )}
            </CredentialMenuDetailRow>
          ) : null}
        </VStack>
      </VStack>
    </Box>
  );
}
